import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import img from "./logo";

const generatePDF = ({ customer, selectedItems, categoryNames, customItems, total, discountAmount, discountPercent, grandTotal, includeGst = false }, action = "download") => {
  
  // Priority: passed amount -> calc from percent -> 0
  let finalDiscountAmount = 0;
  
  const dAmount = parseFloat(discountAmount || 0);
  const dPercent = parseFloat(discountPercent || 0);

  if (dAmount > 0) {
      finalDiscountAmount = dAmount;
  } else if (dPercent > 0) {
      finalDiscountAmount = (total * dPercent) / 100;
  }

  const finalTotal = grandTotal !== undefined ? grandTotal : (total - finalDiscountAmount); // Use passed grandTotal or calc
  const doc = new jsPDF();

  // 🖼️ Add logo to top-left
  try {
      doc.addImage(img, "PNG", 14, 10, 20, 30); // (x, y, width, height)
  } catch(e) {
      console.warn("Logo add failed", e);
  }

  // 🏢 Company Info aligned to right
  doc.setFontSize(12);
  doc.text("AVANTICA PUMPS PVT. LTD.", 200 - 14, 15, { align: "right" });
  doc.setFontSize(8);
  doc.text("PLOT NO. 75/2, AT. MAGHASAR, Nr. NAVJIVAN HOTEL,", 200 - 14, 20, { align: "right" });
  doc.text("Behind IOCL PETROL PUMP, Halol, Gujarat 389350", 200 - 14, 25, { align: "right" });
  doc.text("Phone: +91 7984042112  |  +91 9998546224", 200 - 14, 30, { align: "right" });
  doc.text("Email: sales@avanticapumps.com", 200 - 14, 35, { align: "right" });
  doc.text("Website: www.avanticapumps.com", 200 - 14, 40, { align: "right" });

  // Line below header
  doc.setLineWidth(0.5);
  doc.line(14, 45, 196, 45);

  // 📄 Quotation Heading
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  const title = customer.quotationNo ? `QUOTATION: ${customer.quotationNo}` : "QUOTATION";
  doc.text(title, 105, 55, { align: "center" });

  // 🧾 Customer Info
  let startY = 65;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  // doc.text(`Quotation No: ${customer.quotationNo || 'Draft'}`, 14, startY); // Already in title
  doc.text(`Date: ${customer.date}`, 14, startY);
  doc.text(`Customer: ${customer.name}`, 14, startY + 6);
  doc.text(`City: ${customer.city}`, 14, startY + 12);
  doc.text(`Mobile: ${customer.mobile}`, 14, startY + 18);

  // 🧮 Item Table
  const tableStartY = startY + 30;
  
  // Helper: Determine unit based on category/name
  const getUnit = (category, name) => {
    const text = (category + " " + name).toLowerCase();
    if (text.includes("pipe") || text.includes("cable") || text.includes("wire")) {
      return "Mtr";
    }
    return "Nos";
  };

  // 1. Fixed Items
  const fixedRows = (selectedItems || [])
    .map((item, index) => {
      if (item && item.quantity > 0 && item.name) {
        const category = item.category || (categoryNames && categoryNames[index]) || 'Unknown';
        const unit = getUnit(category, item.name);
        return [
            category,
            item.description ? `${item.name}\n(${item.description})` : item.name,
            `${item.quantity} ${unit}`, // Append unit
            Math.round(includeGst ? item.price / 1.18 : item.price),
            Math.round((includeGst ? item.price / 1.18 : item.price) * parseInt(item.quantity))
        ];
      }
      return null;
    })
    .filter(Boolean);

  // 2. Custom Items (if any)
  const customRows = (customItems || []).map(item => {
      const qty = parseInt(item.quantity || "0", 10);
      const price = parseFloat(item.price || "0");
      if (qty <= 0) return null;
      
      const unit = getUnit("Custom", item.name); // Infer from name for custom items

      return [
        "Custom",
        item.description ? `${item.name}\n(${item.description})` : item.name,
        `${qty} ${unit}`, // Append unit
        Math.round(includeGst ? price / 1.18 : price),
        Math.round((includeGst ? price / 1.18 : price) * qty)
      ];
  }).filter(Boolean);

  const tableData = [...fixedRows, ...customRows].map((row, i) => [i + 1, ...row]);
    autoTable(doc, {
      head: [["Sr.", "Category", "Item Name", "Quantity", "Price", "Total"]],
      body: tableData,
      startY: tableStartY,
      theme: "striped",
      styles: { fontSize: 10 },
      headStyles: {
        fillColor: [0, 51, 102],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 13,
        fillColor: [255, 255, 255], // White background
        textColor: [0, 0, 0],       // Black text
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        4: { halign: 'right' },
        5: { halign: 'right' }
      }
    });
    

  // 💰 Totals
  let finalY = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  
  if (includeGst) {
      const taxableAmount = finalTotal / 1.18;
      const totalGst = finalTotal - taxableAmount;
      const cgst = totalGst / 2;
      const sgst = totalGst / 2;

      if (finalDiscountAmount > 0) {
          doc.text(`Discount: -${finalDiscountAmount.toFixed(2)}`, 196, finalY, { align: "right" });
          finalY += 7;
      }
      doc.text(`Taxable Amount: ${taxableAmount.toFixed(2)}`, 196, finalY, { align: "right" });
      finalY += 7;
      doc.text(`CGST (9%): ${cgst.toFixed(2)}`, 196, finalY, { align: "right" });
      finalY += 7;
      doc.text(`SGST (9%): ${sgst.toFixed(2)}`, 196, finalY, { align: "right" });
      finalY += 8;
  } else {
      doc.text(`Total Amount: ${total.toFixed(2)}`, 196, finalY, { align: "right" });
      finalY += 7;
      if (finalDiscountAmount > 0) {
          doc.text(`Discount: -${finalDiscountAmount.toFixed(2)}`, 196, finalY, { align: "right" });
          finalY += 7;
      }
      finalY += 1;
  }
  
  doc.setFontSize(14);
  doc.text(`Net Payable: ${finalTotal.toFixed(2)}`, 196, finalY, { align: "right" });

  // 🖋️ Footer
  finalY += 15;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("For Avantica Pumps Pvt. Ltd.", 14, finalY + 25);
  doc.text("Authorized Signatory", 14, finalY + 35);

  // 💾 Action Check
  if (action === "print") {
      doc.autoPrint();
      const blob = doc.output("blob");
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
  } else if (action === "preview") {
      return URL.createObjectURL(doc.output("blob"));
  } else if (action === "base64") {
      const dataUri = doc.output("datauristring");
      return dataUri.split(",")[1];
  } else {
      doc.save(`Quotation_${customer.name}_${customer.date}.pdf`);
  }
};

export default generatePDF;
